import type { Game, GameState, ListGamesResponse, LiveStatus, Player, ScoreEvent, Team } from '../types';

// Used in dev mode when VITE_API_BASE is empty. Lets you see the UI
// without a deployed AWS backend.

const days = (n: number) =>
  new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString();

export const mockLiveStatus: LiveStatus = {
  isLive: false,
  channelArn: 'arn:aws:ivs:us-west-2:000000000000:channel/mock',
};

export const mockTeams: Team[] = [
  {
    teamId: 'mock-team',
    name: 'RA Legends 10u',
    slug: 'ra-legends-10u',
    season: '2026 Spring',
    logoUrl: undefined,
    primaryColor: '#10b981',
    secondaryColor: '#f8fafc',
    city: 'Boise',
    state: 'ID',
    leagueName: 'Treasure Valley Youth Baseball',
    division: '10u Majors',
    active: true,
    createdAt: days(30),
  },
];

export const mockPlayers: Player[] = [
  {
    playerId: 'p-00',
    teamId: 'mock-team',
    firstName: 'Mason',
    lastName: 'Rivera',
    name: 'Mason Rivera',
    jerseyNumber: '00',
    primaryPositions: ['P', 'SS'],
    bats: 'R',
    throws: 'R',
    active: true,
    createdAt: days(30),
  },
  {
    playerId: 'p-7',
    teamId: 'mock-team',
    firstName: 'Eli',
    lastName: 'Parker',
    name: 'Eli Parker',
    jerseyNumber: '7',
    primaryPositions: ['C', '3B'],
    bats: 'L',
    throws: 'R',
    active: true,
    createdAt: days(30),
  },
  {
    playerId: 'p-12',
    teamId: 'mock-team',
    firstName: 'Noah',
    lastName: 'Kim',
    name: 'Noah Kim',
    jerseyNumber: '12',
    primaryPositions: ['CF', 'P'],
    bats: 'R',
    throws: 'R',
    active: true,
    createdAt: days(30),
  },
  {
    playerId: 'p-22',
    teamId: 'mock-team',
    firstName: 'Caleb',
    lastName: 'Stone',
    name: 'Caleb Stone',
    jerseyNumber: '22',
    primaryPositions: ['1B', 'RF'],
    bats: 'R',
    throws: 'L',
    active: true,
    createdAt: days(30),
  },
];

const mockGames: Game[] = [
  {
    gameId: 'mock-001',
    teamId: 'mock-team',
    scheduledAt: days(2),
    date: days(2),
    opponent: 'Eastside Eagles',
    gameType: 'REGULAR_SEASON',
    livePrivacy: 'PUBLIC',
    aiAssisted: false,
    status: 'READY',
    s3Prefix: 'mock/2026/05/06/abc',
    vodUrl:
      'https://test-streams.mux.dev/x36xhzz/url_2/url_525/193039199_mp4_h264_aac_hd_7.m3u8',
    duration: 5430,
    createdAt: days(3),
  },
  {
    gameId: 'mock-002',
    teamId: 'mock-team',
    scheduledAt: days(9),
    date: days(9),
    opponent: 'Westgate Wildcats',
    gameType: 'REGULAR_SEASON',
    livePrivacy: 'PUBLIC',
    aiAssisted: false,
    status: 'READY',
    s3Prefix: 'mock/2026/04/29/def',
    vodUrl:
      'https://test-streams.mux.dev/x36xhzz/url_2/url_525/193039199_mp4_h264_aac_hd_7.m3u8',
    duration: 4980,
    createdAt: days(10),
  },
  {
    gameId: 'mock-003',
    teamId: 'mock-team',
    scheduledAt: days(16),
    date: days(16),
    opponent: 'Northridge Knights',
    gameType: 'REGULAR_SEASON',
    livePrivacy: 'PUBLIC',
    aiAssisted: false,
    status: 'FAILED',
    createdAt: days(17),
  },
];

export const mockListGames: ListGamesResponse = { games: mockGames };

export const mockGameById = (id: string): Game | undefined =>
  mockGames.find((g) => g.gameId === id);

export const mockGameState = (gameId: string): GameState => ({
  gameId,
  inning: 4,
  halfInning: 'BOT',
  outs: 1,
  balls: 2,
  strikes: 1,
  runsHome: 4,
  runsAway: 3,
  eventCount: 4,
  lastEventAt: new Date().toISOString(),
  ended: false,
});

export const mockGameEvents = (gameId: string): ScoreEvent[] => [
  {
    gameId,
    seq: 1,
    serverTs: days(2),
    type: 'PLAY',
    play: '1B',
    runsScored: 1,
    videoTimestampSec: 18,
    source: 'HUMAN',
    stateAfter: { ...mockGameState(gameId), inning: 1, halfInning: 'TOP', runsHome: 0, runsAway: 1, eventCount: 1 },
  },
  {
    gameId,
    seq: 2,
    serverTs: days(2),
    type: 'PLAY',
    play: 'OUT',
    videoTimestampSec: 42,
    source: 'HUMAN',
    stateAfter: { ...mockGameState(gameId), inning: 1, halfInning: 'BOT', runsHome: 0, runsAway: 1, outs: 1, eventCount: 2 },
  },
  {
    gameId,
    seq: 3,
    serverTs: days(2),
    type: 'PLAY',
    play: '2B',
    runsScored: 2,
    videoTimestampSec: 78,
    source: 'HUMAN',
    stateAfter: { ...mockGameState(gameId), inning: 2, halfInning: 'BOT', runsHome: 2, runsAway: 1, outs: 0, eventCount: 3 },
  },
  {
    gameId,
    seq: 4,
    serverTs: days(2),
    type: 'PLAY',
    play: 'HR',
    runsScored: 2,
    videoTimestampSec: 118,
    source: 'HUMAN',
    stateAfter: mockGameState(gameId),
  },
];
